import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./BugReports.scss";

const STATUS_CONFIG = {
  REPORTED: { label: "Reported", color: "#f59e0b", bg: "#fef3c7" },
  IN_PROGRESS: { label: "In Progress", color: "#3b82f6", bg: "#dbeafe" },
  FIXED: { label: "Fixed", color: "#10b981", bg: "#d1fae5" },
  CLOSED: { label: "Closed", color: "#6b7280", bg: "#f3f4f6" },
};

const getAuthHeaders = () => {
  const jwtToken = sessionStorage.getItem("jwtToken");
  return {
    Authorization: jwtToken,
    "Content-Type": "application/json",
  };
};

const BugReports = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBug, setSelectedBug] = useState(null);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [updatingId, setUpdatingId] = useState(null);
  const [editAssignee, setEditAssignee] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const fetchBugs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/bugs-report`,
        { headers: getAuthHeaders() },
      );
      setBugs(response.data?.data || []);
    } catch (err) {
      setError("Failed to load bug reports. Please try again.");
      console.error("Error fetching bugs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSelectedBug(null);
    };
    if (selectedBug) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedBug]);

  // Prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selectedBug ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedBug]);

  const updateBugStatus = async (id, status, assignedTo) => {
    try {
      setUpdatingId(id);
      const params = new URLSearchParams({ status });
      if (assignedTo) params.append("assignedTo", assignedTo);

      await axios.put(
        `${process.env.REACT_APP_URL}/api/bugs-report/${id}/status?${params}`,
        {},
        { headers: getAuthHeaders() },
      );

      setBugs((prev) =>
        prev.map((b) =>
          b.id === id
            ? {
                ...b,
                status,
                assignedTo: assignedTo || b.assignedTo,
                resolvedAt:
                  status === "FIXED" || status === "CLOSED"
                    ? new Date().toISOString()
                    : b.resolvedAt,
              }
            : b,
        ),
      );

      if (selectedBug?.id === id) {
        setSelectedBug((prev) => ({
          ...prev,
          status,
          assignedTo: assignedTo || prev.assignedTo,
        }));
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update bug status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredBugs = bugs.filter((bug) => {
    const matchesStatus = filterStatus === "ALL" || bug.status === filterStatus;
    const matchesSearch =
      !searchQuery ||
      bug.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bug.reportedBy?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bug.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: bugs.length,
    reported: bugs.filter((b) => b.status === "REPORTED").length,
    inProgress: bugs.filter((b) => b.status === "IN_PROGRESS").length,
    fixed: bugs.filter((b) => b.status === "FIXED").length,
    closed: bugs.filter((b) => b.status === "CLOSED").length,
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="bug-reports">
      {/* Header */}
      <div className="bug-reports__header">
        <div className="bug-reports__title-row">
          <div>
            <h1 className="bug-reports__title">
              <span className="bug-icon">🐛</span> Bug Reports
            </h1>
            <p className="bug-reports__subtitle">
              Track and manage all reported issues
            </p>
          </div>
          <button
            className="btn-refresh"
            onClick={fetchBugs}
            disabled={loading}
          >
            <span className={loading ? "spin" : ""}>↻</span> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="bug-reports__stats">
          {[
            {
              label: "Total",
              value: stats.total,
              key: "ALL",
              color: "#6366f1",
            },
            {
              label: "Reported",
              value: stats.reported,
              key: "REPORTED",
              color: "#f59e0b",
            },
            {
              label: "In Progress",
              value: stats.inProgress,
              key: "IN_PROGRESS",
              color: "#3b82f6",
            },
            {
              label: "Fixed",
              value: stats.fixed,
              key: "FIXED",
              color: "#10b981",
            },
            {
              label: "Closed",
              value: stats.closed,
              key: "CLOSED",
              color: "#6b7280",
            },
          ].map((stat) => (
            <button
              key={stat.key}
              className={`stat-card ${filterStatus === stat.key ? "active" : ""}`}
              style={{ "--accent": stat.color }}
              onClick={() => setFilterStatus(stat.key)}
            >
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bug-reports__toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by title, reporter, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery("")}>
              ✕
            </button>
          )}
        </div>
        <span className="result-count">
          {filteredBugs.length} bug{filteredBugs.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div className="bug-reports__table-wrap">
        {loading ? (
          <div className="state-empty">
            <div className="loader" />
            <p>Loading bug reports...</p>
          </div>
        ) : error ? (
          <div className="state-empty error">
            <span className="state-icon">⚠️</span>
            <p>{error}</p>
            <button className="btn-retry" onClick={fetchBugs}>
              Retry
            </button>
          </div>
        ) : filteredBugs.length === 0 ? (
          <div className="state-empty">
            <span className="state-icon">✅</span>
            <p>No bug reports found.</p>
          </div>
        ) : (
          <table className="bug-table">
            <thead>
              <tr>
                <th>SL No.</th>
                <th>Title</th>
                <th>Reported By</th>
                <th>Change Status</th>
                <th>Assigned To</th>
                <th>Reported At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBugs.map((bug) => (
                <tr key={bug.id} className="bug-row">
                  <td className="col-id">#{bug.id}</td>
                  <td className="col-title">
                    <span className="title-text">{bug.title}</span>
                  </td>
                  <td className="col-reporter">
                    <div className="reporter-badge">
                      <span className="reporter-avatar">
                        {bug.reportedBy?.[0]?.toUpperCase() || "?"}
                      </span>
                      {bug.reportedBy || "—"}
                    </div>
                  </td>
                  <td
                    className="col-status"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-status-buttons">
                      {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                        <button
                          key={key}
                          className={`inline-status-btn ${bug.status === key ? "active" : ""}`}
                          style={{ "--color": val.color, "--bg": val.bg }}
                          disabled={updatingId === bug.id}
                          onClick={() =>
                            updateBugStatus(bug.id, key, bug.assignedTo)
                          }
                          title={val.label}
                        >
                          {val.label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td
                    className="col-assignee"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="assignee-input-wrap">
                      <input
                        type="text"
                        className="assignee-input"
                        placeholder="Unassigned"
                        value={editAssignee[bug.id] ?? (bug.assignedTo || "")}
                        onChange={(e) =>
                          setEditAssignee((prev) => ({
                            ...prev,
                            [bug.id]: e.target.value,
                          }))
                        }
                        onBlur={() => {
                          const val = editAssignee[bug.id];
                          if (
                            val !== undefined &&
                            val !== (bug.assignedTo || "")
                          ) {
                            updateBugStatus(bug.id, bug.status, val);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                      />
                    </div>
                  </td>
                  <td className="col-date">{formatDate(bug.reportedAt)}</td>
                  <td className="col-actions">
                    <button
                      className="btn-view"
                      onClick={() => setSelectedBug(bug)}
                      title="View details"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal Popup ── */}
      {selectedBug && (
        <div className="bug-modal-overlay" onClick={() => setSelectedBug(null)}>
          <div className="bug-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bug-modal__header">
              <div className="bug-modal__header-left">
                <span className="bug-modal__id">Bug #{selectedBug.id}</span>
                <span
                  className="status-badge"
                  style={{
                    color: STATUS_CONFIG[selectedBug.status]?.color,
                    backgroundColor: STATUS_CONFIG[selectedBug.status]?.bg,
                  }}
                >
                  {STATUS_CONFIG[selectedBug.status]?.label}
                </span>
              </div>
              <button
                className="bug-modal__close"
                onClick={() => setSelectedBug(null)}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="bug-modal__body">
              <div className="modal-section">
                <label>Title</label>
                <p className="modal-value modal-title">{selectedBug.title}</p>
              </div>

              <div className="modal-section">
                <label>Description</label>
                <p className="modal-value modal-description">
                  {selectedBug.description || "No description provided."}
                </p>
              </div>

              <div className="modal-meta-grid">
                <div className="modal-section">
                  <label>Reported By</label>
                  <div className="reporter-badge">
                    <span className="reporter-avatar">
                      {selectedBug.reportedBy?.[0]?.toUpperCase() || "?"}
                    </span>
                    <span className="modal-value">
                      {selectedBug.reportedBy || "—"}
                    </span>
                  </div>
                </div>

                <div className="modal-section">
                  <label>Assigned To</label>
                  <p className="modal-value">
                    {selectedBug.assignedTo || "Unassigned"}
                  </p>
                </div>

                <div className="modal-section">
                  <label>Reported At</label>
                  <p className="modal-value">
                    {formatDate(selectedBug.reportedAt)}
                  </p>
                </div>

                {selectedBug.resolvedAt && (
                  <div className="modal-section">
                    <label>Resolved At</label>
                    <p className="modal-value">
                      {formatDate(selectedBug.resolvedAt)}
                    </p>
                  </div>
                )}
              </div>

              {/* Status Update */}
              <div className="modal-section">
                <label>Update Status</label>
                <div className="status-buttons">
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <button
                      key={key}
                      className={`status-btn ${selectedBug.status === key ? "active" : ""}`}
                      style={{ "--color": val.color, "--bg": val.bg }}
                      disabled={updatingId === selectedBug.id}
                      onClick={() =>
                        updateBugStatus(
                          selectedBug.id,
                          key,
                          selectedBug.assignedTo,
                        )
                      }
                    >
                      {val.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bug-modal__footer">
              <button
                className="btn-modal-close"
                onClick={() => setSelectedBug(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BugReports;
